import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Archive, Bot, MessageSquare } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const legacyArticles = [
  {
    title: 'SweetPilot Legacy',
    href: '/doc/article/sweetpilot-legacy',
    icon: Archive,
    description: 'Learn about the original SweetPilot platform and how it evolved into what it is today.',
  },
  {
    title: 'SweetPilot Bots',
    href: '/doc/article/sweetpilot-bots',
    icon: Bot,
    description: 'Discover the automated bots that powered early SweetPilot workflows.',
  },
  {
    title: 'SweetPilot Prompts',
    href: '/doc/article/sweetpilot-prompts',
    icon: MessageSquare,
    description: 'Explore the prompt library that helped users get the most out of AI.',
  },
];

interface ArticleItemProps {
  title: string;
  href: string;
  icon: React.ElementType;
  description: string;
  isDark: boolean;
}

function ArticleItem({ title, href, icon: Icon, description, isDark }: ArticleItemProps) {
  return (
    <Link
      to={href}
      className={`flex items-center justify-between py-5 px-4 transition-all group border-b ${
        isDark
          ? 'border-white/5 hover:bg-white/5'
          : 'border-slate-100 hover:bg-slate-50'
      }`}
    >
      <div className="flex items-center gap-4">
        <div className={`p-2 rounded-lg ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
          <Icon className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
        </div>
        <div>
          <span className={`font-medium transition-colors block ${
            isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
          }`}>
            {title}
          </span>
          <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
            {description}
          </p>
        </div>
      </div>
      <ChevronRight className={`w-5 h-5 transition-colors flex-shrink-0 ${
        isDark ? 'text-gray-600 group-hover:text-purple-400' : 'text-slate-300 group-hover:text-purple-600'
      }`} />
    </Link>
  );
}

export default function LegacyProducts() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title=""
      subtitle=""
      showBackButton={false}
    >
      {/* Breadcrumbs */}
      <nav className="mb-8">
        <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <li>
            <Link
              to="/docs"
              className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
            >
              All Collections
            </Link>
          </li>
          <li>
            <ChevronRight className="w-4 h-4" />
          </li>
          <li>
            <Link
              to="/doc/collections/getting-started"
              className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
            >
              Getting started
            </Link>
          </li>
          <li>
            <ChevronRight className="w-4 h-4" />
          </li>
          <li>
            <Link
              to="/doc/collections/getting-started"
              className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
            >
              General info
            </Link>
          </li>
          <li>
            <ChevronRight className="w-4 h-4" />
          </li>
          <li className={isDark ? 'text-white' : 'text-slate-900'}>
            SweetPilot AI Legacy products
          </li>
        </ol>
      </nav>

      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className={`text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          SweetPilot AI Legacy products
        </h1>
        <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Prompts and bots offered by SweetPilot before SweetPilot App existed.
        </p>
      </motion.div>

      {/* Articles List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`rounded-2xl border overflow-hidden ${
          isDark
            ? 'bg-dark-800/50 border-white/5'
            : 'bg-white border-slate-200 shadow-sm'
        }`}
      >
        {legacyArticles.map((article) => (
          <ArticleItem
            key={article.title}
            title={article.title}
            href={article.href}
            icon={article.icon}
            description={article.description}
            isDark={isDark}
          />
        ))}
      </motion.div>

      {/* Info Box */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className={`mt-8 p-5 rounded-xl border ${
          isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'
        }`}
      >
        <div className="flex items-start gap-3">
          <Archive className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          <div className={`${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
            <p className="mb-2">
              <strong className={isDark ? 'text-white' : 'text-slate-900'}>About Legacy Products</strong>
            </p>
            <p className="text-sm">
              These products represent SweetPilot's journey from simple AI tools to the comprehensive AI employee platform you see today. While some features have been deprecated or integrated into the main app, understanding our legacy helps you appreciate the full capabilities of the current platform.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Back to Getting Started */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-8"
      >
        <Link
          to="/doc/collections/getting-started"
          className={`inline-flex items-center gap-2 text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Back to Getting Started
        </Link>
      </motion.div>

      <div className="h-16" />

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-center"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">🤖</span>
        </div>
        <a
          href="mailto:help@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          help@sweetpilot.com
        </a>
      </motion.div>
    </StaticPageLayout>
  );
}
